// ActionCentre.js
// Fleet-wide Action Centre — full-page view opened from the Head Office card.
//
// Three stacked sections:
//   1. Messages awaiting acknowledgement  (oldest first)
//        date · pharmacy · recipient · subject · content (hover → full popup) · status
//   2. Current possible messages to send
//        per-pharmacy suggestions with Include + Require-acknowledgement
//        checkboxes, "Add another message" per pharmacy, "Add message to all
//        pharmacies" at the top, a Preview per pharmacy, and a "Send message to
//        all pharmacies" button. On send: ack-required messages move to grid 1,
//        the rest move to grid 3.
//   3. Acknowledged / no-acknowledgement-required messages  (reverse chrono)
//        same columns as grid 1, with a search box (searches the whole entry)
//        and a pharmacy filter, loaded in pages.
//
// Near-miss alerts from /near_miss_alerts_fleet seed the per-pharmacy
// suggestions where available; the rest of the suggestion text and all
// acknowledgement tracking is front-end mock / in-session state until a
// messaging backend exists.

import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  displayName,
  useMaskPharmacyNames,
  MaskToggleButton,
} from "./nameMask";

const API_BASE = "https://api.pharmacegroup.co.uk";
const PAGE_SIZE = 15;

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */
function fmtDateTime(d) {
  if (!d) return "—";
  const t = new Date(d);
  if (isNaN(t.getTime())) return "—";
  return t.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) +
    " " + t.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

let _uid = 0;
const nextId = () => `m${Date.now()}_${++_uid}`;

// Default suggestion templates used to seed each pharmacy's possible messages.
const SUGGESTION_TEMPLATES = [
  { subject: "Near-miss review reminder", body: "We've noticed this month's near-miss reviews are still outstanding. Could the team complete them when there's a moment? Happy to help if anything's unclear." },
  { subject: "SOP read-and-sign overdue", body: "A couple of SOPs are showing as unread past their due date. A quick read-and-sign from the team would bring the branch back up to date." },
  { subject: "Fridge log check", body: "The fridge temperature log has a gap in recent days. Could someone confirm the readings are being recorded? Let us know if the probe needs attention." }
];

/* ------------------------------------------------------------------ */
/* Shared: a read-only message grid (used by grids 1 and 3)           */
/* ------------------------------------------------------------------ */
function MessageGrid({ rows, statusLabel, statusColor, oldestFirst, masked, onHoverContent }) {
  const th = {
    textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: 0.4, color: "#6b7280",
    borderBottom: "2px solid #e5e7eb", whiteSpace: "nowrap"
  };
  const td = { padding: "9px 12px", fontSize: 13, borderBottom: "1px solid #f0f1f3", verticalAlign: "top" };

  const ordered = [...rows].sort((a, b) => {
    const ta = new Date(a.sentAt || a.createdAt || 0).getTime();
    const tb = new Date(b.sentAt || b.createdAt || 0).getTime();
    return oldestFirst ? ta - tb : tb - ta;
  });

  return (
    <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead style={{ background: "#f9fafb" }}>
          <tr>
            <th style={th}>Date</th>
            <th style={th}>Pharmacy</th>
            <th style={th}>Recipient</th>
            <th style={th}>Subject</th>
            <th style={th}>Content</th>
            <th style={{ ...th, textAlign: "right" }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {ordered.length === 0 ? (
            <tr><td colSpan={6} style={{ ...td, textAlign: "center", color: "#9ca3af", padding: 20 }}>No messages.</td></tr>
          ) : ordered.map(m => (
            <tr key={m.id}>
              <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDateTime(m.sentAt || m.createdAt)}</td>
              <td style={{ ...td, fontWeight: 600 }}>{displayName(m.pharmacyName, masked)}</td>
              <td style={td}>{m.recipientName || "—"}</td>
              <td style={td}>{m.subject || "—"}</td>
              <td style={td}>
                <div
                  onMouseEnter={e => onHoverContent(m, e)}
                  onMouseLeave={() => onHoverContent(null)}
                  style={{
                    maxWidth: 280, maxHeight: 44, overflow: "hidden",
                    textOverflow: "ellipsis", fontSize: 12, color: "#4b5563",
                    border: "1px solid #e5e7eb", borderRadius: 4, padding: "4px 6px",
                    background: "#fafafa", cursor: "help", lineHeight: 1.4
                  }}
                >
                  {m.body}
                </div>
              </td>
              <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                {(() => {
                  const sc = typeof statusColor === "function" ? statusColor(m) : statusColor;
                  return (
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 999,
                      color: sc.text, background: sc.bg
                    }}>
                      {typeof statusLabel === "function" ? statusLabel(m) : statusLabel}
                    </span>
                  );
                })()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Content hover popup                                                */
/* ------------------------------------------------------------------ */
function ContentPopup({ hover }) {
  if (!hover) return null;
  const { msg, x, y } = hover;
  return (
    <div style={{
      position: "fixed", left: Math.min(x + 14, window.innerWidth - 360), top: Math.min(y + 14, window.innerHeight - 200),
      width: 340, maxHeight: 260, overflow: "auto", zIndex: 2000,
      background: "#1f2937", color: "#f9fafb", borderRadius: 8, padding: "12px 14px",
      boxShadow: "0 12px 30px rgba(0,0,0,0.35)", fontSize: 13, lineHeight: 1.5, pointerEvents: "none"
    }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{msg.subject}</div>
      <div style={{ whiteSpace: "pre-wrap" }}>{msg.body}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Preview dialog (for one pharmacy's selected messages)              */
/* ------------------------------------------------------------------ */
function PreviewDialog({ pharmacy, recipientName, messages, requireAck, onClose }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1500 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(640px, 95vw)", maxHeight: "88vh", background: "#fff", borderRadius: 8, display: "flex", flexDirection: "column", boxShadow: "0 20px 40px rgba(0,0,0,0.2)" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #eee" }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Preview — {pharmacy}</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>To: {recipientName || "branch team"}</div>
        </div>
        <div style={{ padding: 20, overflow: "auto", flex: 1, fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
          {messages.length === 0 ? (
            <p style={{ fontStyle: "italic", color: "#6b7280" }}>No messages selected for this pharmacy.</p>
          ) : (
            <>
              <p style={{ marginTop: 0 }}>Hello {recipientName || "team"},</p>
              <p>We wanted to share a few points with you to help keep everything on track at {pharmacy}:</p>
              <ul style={{ paddingLeft: 22 }}>
                {messages.map(m => (
                  <li key={m.id} style={{ marginBottom: 10 }}>
                    <strong>{m.subject}</strong>{m.body ? <>: {m.body}</> : null}
                  </li>
                ))}
              </ul>
              {requireAck && (
                <p style={{ fontWeight: 600 }}>
                  Please let us know when these items have been actioned by replying to this message.
                </p>
              )}
              <p>If anything needs a hand, just let us know — we're here to help.</p>
              <p>With thanks,<br />The Pharmace team</p>
            </>
          )}
        </div>
        <div style={{ padding: "10px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ fontSize: 12, padding: "6px 16px", border: "1px solid #9ca3af", borderRadius: 4, background: "#f3f4f6", cursor: "pointer" }}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Confirm-send dialog — lists pharmacies with something ticked,      */
/* message visible on hover, Proceed / Cancel.                        */
/* ------------------------------------------------------------------ */
function ConfirmSendDialog({ summary, masked, sending, onProceed, onCancel }) {
  const [hover, setHover] = useState(null);
  const totalMessages = summary.reduce((n, s) => n + s.messages.length, 0);

  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1600 }}
      onMouseMove={e => hover && setHover(h => h ? { ...h, x: e.clientX, y: e.clientY } : h)}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(560px, 95vw)", maxHeight: "85vh", background: "#fff", borderRadius: 8, display: "flex", flexDirection: "column", boxShadow: "0 20px 40px rgba(0,0,0,0.2)" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #eee" }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Confirm send</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>
            {totalMessages} message{totalMessages === 1 ? "" : "s"} to {summary.length} pharmac{summary.length === 1 ? "y" : "ies"}. Hover a message to read it.
          </div>
        </div>

        <div style={{ padding: 14, overflow: "auto", flex: 1 }}>
          {summary.map((s, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                {displayName(s.pharmacy, masked)}
                <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 8 }}>→ {s.recipientName}</span>
                {!s.pharmacyId && <span style={{ color: "#dc2626", fontSize: 11, marginLeft: 8 }}>(no id — cannot send)</span>}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {s.messages.map((m, j) => (
                  <span
                    key={j}
                    onMouseEnter={e => setHover({ m, x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setHover(null)}
                    style={{
                      fontSize: 12, padding: "3px 10px", borderRadius: 999, cursor: "help",
                      background: m.requireAck ? "#fef3c7" : "#eef2ff",
                      color: m.requireAck ? "#92400e" : "#3730a3",
                      border: "1px solid " + (m.requireAck ? "#fde68a" : "#c7d2fe")
                    }}
                  >
                    {m.subject || "(no subject)"}{m.requireAck ? " · ack" : ""}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: "10px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} disabled={sending} style={{ fontSize: 12, padding: "6px 16px", border: "1px solid #9ca3af", borderRadius: 4, background: "#f3f4f6", cursor: "pointer" }}>Cancel</button>
          <button onClick={onProceed} disabled={sending} style={{ fontSize: 12, padding: "6px 18px", border: "1px solid #1f2937", borderRadius: 4, background: sending ? "#9ca3af" : "#1f2937", color: "#fff", fontWeight: 600, cursor: sending ? "wait" : "pointer" }}>
            {sending ? "Sending…" : "Proceed"}
          </button>
        </div>
      </div>

      {hover && (
        <div style={{
          position: "fixed", left: Math.min(hover.x + 14, window.innerWidth - 360), top: Math.min(hover.y + 14, window.innerHeight - 180),
          width: 340, maxHeight: 220, overflow: "auto", zIndex: 2100,
          background: "#1f2937", color: "#f9fafb", borderRadius: 8, padding: "12px 14px",
          boxShadow: "0 12px 30px rgba(0,0,0,0.35)", fontSize: 13, lineHeight: 1.5, pointerEvents: "none"
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{hover.m.subject || "(no subject)"}</div>
          <div style={{ whiteSpace: "pre-wrap" }}>{hover.m.body || "(no body)"}</div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Compose dialog for "Add message to all pharmacies"                 */
/* ------------------------------------------------------------------ */
function AddToAllDialog({ pharmacyCount, onCancel, onAdd }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const submit = () => {
    if (!subject.trim() && !body.trim()) {
      window.alert("The message is empty. Add a subject or body before adding it to all pharmacies.");
      return;
    }
    onAdd({ subject: subject.trim() || "(no subject)", body: body.trim() });
  };

  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1600 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(560px, 95vw)", background: "#fff", borderRadius: 8, display: "flex", flexDirection: "column", boxShadow: "0 20px 40px rgba(0,0,0,0.2)" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #eee" }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Add message to all pharmacies</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>
            This message will be added (ticked) to {pharmacyCount} pharmac{pharmacyCount === 1 ? "y" : "ies"}. You can still edit or untick it per pharmacy before sending.
          </div>
        </div>

        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Subject</label>
            <input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Subject"
              style={{ width: "100%", fontSize: 13, fontWeight: 600, border: "1px solid #d1d5db", borderRadius: 4, padding: "6px 8px" }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Message</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Message body"
              rows={5}
              style={{ width: "100%", fontSize: 13, color: "#374151", border: "1px solid #d1d5db", borderRadius: 4, padding: "6px 8px", resize: "vertical", fontFamily: "inherit" }}
            />
          </div>
        </div>

        <div style={{ padding: "10px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} style={{ fontSize: 12, padding: "6px 16px", border: "1px solid #9ca3af", borderRadius: 4, background: "#f3f4f6", cursor: "pointer" }}>Cancel</button>
          <button onClick={submit} style={{ fontSize: 12, padding: "6px 18px", border: "1px solid #1f2937", borderRadius: 4, background: "#1f2937", color: "#fff", fontWeight: 600, cursor: "pointer" }}>Add message to all pharmacies</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main component                                                     */
/* ------------------------------------------------------------------ */
function ActionCentre() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const masked = useMaskPharmacyNames();

  const viewerOrg = localStorage.getItem("organisationName") || "";
  const isAdmin = viewerOrg === "Pharmace all";

  // Three message stores.
  const [awaiting, setAwaiting] = useState([]);       // grid 1
  const [possible, setPossible] = useState({});       // grid 2: { [pharmacy]: { recipientName, messages:[{id,subject,body,include,requireAck,seed}] } }
  const [acknowledged, setAcknowledged] = useState([]); // grid 3

  // UI state
  const [hover, setHover] = useState(null);
  const [previewFor, setPreviewFor] = useState(null);
  const [search, setSearch] = useState("");
  const [pharmacyFilter, setPharmacyFilter] = useState("");
  const [ackTotal, setAckTotal] = useState(0);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(null); // [{pharmacy, messages:[{subject,body,requireAck}]}] | null
  const [composeAll, setComposeAll] = useState(false);  // "add message to all" compose dialog open

  const tokenRef = useRef(null);

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${tokenRef.current}`
  });
  const orgQS = () => (isAdmin || !viewerOrg) ? "" : `&organisationName=${encodeURIComponent(viewerOrg)}`;

  useEffect(() => {
    tokenRef.current = localStorage.getItem("authToken");
    loadSeed();
    loadAwaiting();
    loadAcknowledged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Load grid 1 (outstanding: not opened, or opened-but-unacknowledged) ----
  const loadAwaiting = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/headoffice/action-centre/messages?status=outstanding&limit=500${orgQS()}`, {
        headers: { Authorization: `Bearer ${tokenRef.current}` }
      });
      if (!res.ok) return;
      const json = await res.json();
      setAwaiting((json.rows || []).map(normaliseRow));
    } catch (_) { /* leave as-is */ }
  };

  // ---- Load grid 3 (done: opened and not-needing-ack or acknowledged) ----
  const loadAcknowledged = async (reset = true) => {
    try {
      const skip = reset ? 0 : acknowledged.length;
      const params = `status=done&limit=${PAGE_SIZE}&skip=${skip}` +
        (search.trim() ? `&search=${encodeURIComponent(search.trim())}` : "") +
        (pharmacyFilter ? `&pharmacyId=${encodeURIComponent(pharmacyFilter)}` : "") +
        orgQS();
      const res = await fetch(`${API_BASE}/api/headoffice/action-centre/messages?${params}`, {
        headers: { Authorization: `Bearer ${tokenRef.current}` }
      });
      if (!res.ok) return;
      const json = await res.json();
      const rows = (json.rows || []).map(normaliseRow);
      setAckTotal(json.total || 0);
      setAcknowledged(prev => reset ? rows : [...prev, ...rows]);
    } catch (_) { /* leave as-is */ }
  };

  // Normalise a backend message doc to the shape the grids render.
  const normaliseRow = (d) => ({
    id: String(d._id),
    pharmacyId: d.pharmacyId ? String(d.pharmacyId) : null,
    pharmacyName: d.pharmacyName,
    recipientName: d.recipientName,
    subject: d.subject,
    body: d.body,
    bullets: Array.isArray(d.bullets) ? d.bullets : [],
    sentAt: d.sentAt,
    createdAt: d.sentAt,
    requireAck: !!d.requireAck,
    opened: !!d.opened,
    openedAt: d.openedAt,
    acknowledged: !!d.acknowledged,
    acknowledgedAt: d.acknowledgedAt
  });

  // Seed grid 2 from real near-miss alerts where possible; fall back to mock.
  const loadSeed = async () => {
    setLoading(true); setError("");
    try {
      const token = tokenRef.current;
      let alerts = [];
      let recipientByPharmacy = {};
      let pharmacyIdByName = {};
      let emailByPharmacy = {};
      if (token) {
        try {
          const res = await fetch(`${API_BASE}/near_miss_alerts_fleet`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ status: "pending" })
          });
          if (res.ok) {
            const json = await res.json();
            alerts = Array.isArray(json.alerts) ? json.alerts : [];
          }
        } catch (_) { /* fall through to mock */ }
      }

      // Org scope.
      if (!isAdmin) alerts = alerts.filter(a => (a.organisationName || "") === viewerOrg);

      // Group alert-derived suggestions by pharmacy.
      const byPharm = {};
      for (const a of alerts) {
        const name = a.pharmacyName || "(unknown branch)";
        if (!byPharm[name]) byPharm[name] = [];
        byPharm[name].push({
          id: nextId(),
          alertId: a._id ? String(a._id) : null,   // source near_miss_alerts doc
          kind: "nearmiss",
          subject: a.insightTitle || "Near-miss insight",
          body: a.summary || a.fullDetail || a.insightTitle || "A near-miss pattern was flagged for review.",
          include: false,
          requireAck: false,
          seed: true
        });
        if (a.recipient && a.recipient.name) recipientByPharmacy[name] = a.recipient.name;
        if (a.pharmacyId) pharmacyIdByName[name] = String(a.pharmacyId);
      }

      // Fetch real RED compliance items per pharmacy and merge them in as
      // additional (unticked) suggestion lines.
      if (token) {
        try {
          const sres = await fetch(`${API_BASE}/api/headoffice/action-centre/suggestions?${orgQS().replace(/^&/, "")}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (sres.ok) {
            const groups = await sres.json();
            for (const g of (Array.isArray(groups) ? groups : [])) {
              const name = g.pharmacyName || "(unknown branch)";
              // Ensure a block exists for EVERY pharmacy returned, even with
              // no current recommendations, so it stays visible and messageable.
              if (!byPharm[name]) byPharm[name] = [];
              if (g.pharmacyId) pharmacyIdByName[name] = String(g.pharmacyId);
              if (g.recipientEmail) emailByPharmacy[name] = g.recipientEmail;
              for (const line of (g.lines || [])) {
                byPharm[name].push({
                  id: nextId(),
                  alertId: null,
                  kind: "summary",
                  moduleKey: line.moduleKey,
                  subject: line.label,
                  body: line.detail,
                  include: false,
                  requireAck: false,
                  seed: true
                });
              }
            }
          }
        } catch (_) { /* compliance suggestions are best-effort */ }
      }

      // If nothing came back, seed a few mock pharmacies so the UI is usable.
      if (Object.keys(byPharm).length === 0) {
        const mockPharmacies = ["Riverside Pharmacy", "Highstreet Chemist", "Parkside Pharmacy", "Canalside Pharmacy"];
        mockPharmacies.forEach((name, idx) => {
          const tmpl = SUGGESTION_TEMPLATES[idx % SUGGESTION_TEMPLATES.length];
          byPharm[name] = [{
            id: nextId(), subject: tmpl.subject, body: tmpl.body,
            include: false, requireAck: false, seed: true
          }];
          recipientByPharmacy[name] = "Branch manager";
        });
      }

      const possibleInit = {};
      for (const [name, messages] of Object.entries(byPharm)) {
        possibleInit[name] = {
          pharmacyId: pharmacyIdByName[name] || null,
          recipientName: recipientByPharmacy[name] || "Branch manager",
          recipientEmail: emailByPharmacy[name] || "",
          requireAck: false,
          messages
        };
      }
      setPossible(possibleInit);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  /* ---------- grid 2 mutations ---------- */
  const updatePossible = (pharmacy, updater) => {
    setPossible(prev => {
      const cur = prev[pharmacy] || { pharmacyId: null, recipientName: "Branch manager", recipientEmail: "", requireAck: false, messages: [] };
      const copy = { pharmacyId: cur.pharmacyId, recipientName: cur.recipientName, recipientEmail: cur.recipientEmail, requireAck: cur.requireAck, messages: cur.messages.map(m => ({ ...m })) };
      updater(copy);
      return { ...prev, [pharmacy]: copy };
    });
  };

  const toggleBlockAck = (pharmacy) => {
    updatePossible(pharmacy, (st) => { st.requireAck = !st.requireAck; });
  };

  const toggleMsg = (pharmacy, id, field) => {
    updatePossible(pharmacy, (st) => {
      const m = st.messages.find(x => x.id === id);
      if (m) m[field] = !m[field];
    });
  };

  const editMsg = (pharmacy, id, field, value) => {
    updatePossible(pharmacy, (st) => {
      const m = st.messages.find(x => x.id === id);
      if (m) m[field] = value;
    });
  };

  const addMessageToPharmacy = (pharmacy) => {
    updatePossible(pharmacy, (st) => {
      st.messages.push({ id: nextId(), subject: "New message", body: "", include: true, requireAck: false, seed: false });
    });
  };

  // Dismiss a near-miss-derived suggestion: mark it dismissed in the
  // near_miss_alerts collection so the nightly engine stops re-raising it,
  // strike it out locally, then drop it on the next load. Non-alert (manually
  // added) rows have no alertId — just remove them locally.
  const dismissAlert = async (pharmacy, id) => {
    let alertId = null;
    setPossible(prev => {
      const block = prev[pharmacy];
      const m = block?.messages.find(x => x.id === id);
      alertId = m?.alertId || null;
      return prev;
    });

    if (!alertId) {
      // Locally-added row — just remove it.
      updatePossible(pharmacy, (st) => {
        st.messages = st.messages.filter(x => x.id !== id);
      });
      return;
    }

    // Optimistically strike it out.
    updatePossible(pharmacy, (st) => {
      const m = st.messages.find(x => x.id === id);
      if (m) { m.dismissed = true; m.include = false; }
    });

    try {
      const res = await fetch(`${API_BASE}/near_miss_alerts_dismiss`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ alertId })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Leave it struck-out for this session; it won't return on next load.
    } catch (e) {
      // Roll back the strike-out on failure.
      updatePossible(pharmacy, (st) => {
        const m = st.messages.find(x => x.id === id);
        if (m) m.dismissed = false;
      });
      window.alert("Dismiss failed: " + (e.message || e));
    }
  };

  // Add a composed message line (auto-ticked) to every pharmacy's grid.
  const addMessageToAll = ({ subject, body }) => {
    setPossible(prev => {
      const next = {};
      for (const [name, block] of Object.entries(prev)) {
        next[name] = {
          ...block,
          messages: [...block.messages.map(m => ({ ...m })),
            { id: nextId(), subject, body, include: true, seed: false }]
        };
      }
      return next;
    });
  };

  /* ---------- sending ---------- */
  // Helper: assemble one pharmacy's ticked lines into a single message.
  // The body mirrors the Preview dialog's letter format exactly.
  const buildMessageFor = (pharmacy, block) => {
    const ticked = block.messages.filter(m => m.include && !m.dismissed);
    if (!ticked.length) return null;
    const bullets = ticked.map(m => ({
      subject: m.subject, body: m.body, alertId: m.alertId || null
    }));
    const requireAck = !!block.requireAck;
    const recipientName = block.recipientName || "team";
    const displayPharmacy = displayName(pharmacy, masked);

    // Subject: single line's subject, or a summary for multiple.
    const subject = ticked.length === 1
      ? (ticked[0].subject || "Message")
      : `${ticked.length} items for your attention`;

    // Body: same supportive letter as the Preview dialog.
    const lines = [];
    lines.push(`Hello ${recipientName},`);
    lines.push("");
    lines.push(`We wanted to share a few points with you to help keep everything on track at ${displayPharmacy}:`);
    lines.push("");
    for (const m of ticked) {
      lines.push(`• ${m.subject}${m.body ? `: ${m.body}` : ""}`);
    }
    lines.push("");
    if (requireAck) {
      lines.push("Please let us know when these items have been actioned by replying to this message.");
      lines.push("");
    }
    lines.push("If anything needs a hand, just let us know — we're here to help.");
    lines.push("");
    lines.push("With thanks,");
    lines.push("The Pharmace team");
    const body = lines.join("\n");

    return {
      pharmacyId: block.pharmacyId,
      pharmacyName: pharmacy,
      recipientName: block.recipientName,
      recipientEmail: block.recipientEmail || "",
      requireAck,
      subject, body, bullets,
      alertIds: ticked.map(m => m.alertId).filter(Boolean),
      localIds: ticked.map(m => m.id)
    };
  };

  // Build the per-pharmacy combined messages, then open the confirm dialog.
  const requestSend = (onlyPharmacy = null) => {
    const summary = [];
    for (const [pharmacy, block] of Object.entries(possible)) {
      if (onlyPharmacy && pharmacy !== onlyPharmacy) continue;
      const msg = buildMessageFor(pharmacy, block);
      if (msg) {
        summary.push({
          pharmacy,
          pharmacyId: msg.pharmacyId,
          recipientName: msg.recipientName,
          requireAck: msg.requireAck,
          // confirm dialog shows the bullet items as the "messages" list
          messages: msg.bullets.map(b => ({ subject: b.subject, body: b.body, requireAck: msg.requireAck }))
        });
      }
    }
    if (summary.length === 0) {
      window.alert(onlyPharmacy
        ? "Nothing is ticked for this pharmacy. Tick the messages you'd like to include first."
        : "No messages are ticked to send. Tick the messages you'd like to include first.");
      return;
    }
    setConfirmSend(summary);
  };

  const sendAll = async () => {
    // The set of pharmacies to send = whatever is in the confirm dialog
    // (all pharmacies for "send to all", or one for "send to this pharmacy").
    const scope = new Set((confirmSend || []).map(s => s.pharmacy));
    if (scope.size === 0) { setConfirmSend(null); return; }

    // One combined message per pharmacy in scope.
    const payload = [];
    const sentLocalIds = []; // {pharmacy, id}
    const sentAlertIds = [];
    for (const [pharmacy, block] of Object.entries(possible)) {
      if (!scope.has(pharmacy)) continue;
      const msg = buildMessageFor(pharmacy, block);
      if (!msg) continue;
      payload.push({
        pharmacyId: msg.pharmacyId,
        pharmacyName: msg.pharmacyName,
        recipientName: msg.recipientName,
        recipientEmail: msg.recipientEmail,
        subject: msg.subject,
        body: msg.body,
        bullets: msg.bullets,
        requireAck: msg.requireAck
      });
      msg.localIds.forEach(id => sentLocalIds.push({ pharmacy, id }));
      msg.alertIds.forEach(id => sentAlertIds.push(id));
    }

    if (payload.length === 0) {
      setConfirmSend(null);
      return;
    }
    if (payload.some(p => !p.pharmacyId)) {
      window.alert("Some selected pharmacies have no pharmacyId resolved, so they can't be sent yet. (These are mock/seed branches without a backend id.)");
      setConfirmSend(null);
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/headoffice/action-centre/messages/send`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ messages: payload })
      });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);

      // Mark the source near-miss alerts 'sent' (no-email endpoint).
      if (sentAlertIds.length) {
        await Promise.all(sentAlertIds.map(alertId =>
          fetch(`${API_BASE}/near_miss_alerts_mark_sent`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({ alertId })
          }).catch(() => null)
        ));
      }

      // Strip sent lines from grid 2, but KEEP the pharmacy block even when
      // it's left empty — so it stays visible ("No current recommendations")
      // and the manager can still message it.
      setPossible(prev => {
        const next = {};
        for (const [pharmacy, block] of Object.entries(prev)) {
          const sentIds = new Set(sentLocalIds.filter(s => s.pharmacy === pharmacy).map(s => s.id));
          const keep = block.messages.filter(m => !sentIds.has(m.id));
          next[pharmacy] = { ...block, messages: keep };
        }
        return next;
      });

      // Refresh grids 1 and 3 from the server.
      await Promise.all([loadAwaiting(), loadAcknowledged(true)]);
    } catch (e) {
      window.alert("Send failed: " + (e.message || e));
    } finally {
      setSending(false);
      setConfirmSend(null);
    }
  };

  // Top-grid status click: advance the message one step.
  //   not opened           -> open
  //   opened, needs ack     -> acknowledge
  const progressRow = async (row) => {
    try {
      const endpoint = !row.opened ? "open" : "acknowledge";
      const res = await fetch(`${API_BASE}/api/headoffice/action-centre/messages/${row.id}/${endpoint}`, {
        method: "POST",
        headers: authHeaders()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await Promise.all([loadAwaiting(), loadAcknowledged(true)]);
    } catch (e) {
      window.alert("Update failed: " + (e.message || e));
    }
  };

  /* ---------- grid 3 (server-driven) ---------- */
  // Re-query grid 3 when search / pharmacy filter change (debounced).
  useEffect(() => {
    const t = setTimeout(() => loadAcknowledged(true), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pharmacyFilter]);

  const pharmacyOptions = useMemo(() => {
    const byId = new Map();
    for (const [name, block] of Object.entries(possible)) {
      if (block.pharmacyId) byId.set(String(block.pharmacyId), name);
    }
    for (const m of [...awaiting, ...acknowledged]) {
      if (m.pharmacyId && !byId.has(m.pharmacyId)) byId.set(m.pharmacyId, m.pharmacyName);
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [possible, awaiting, acknowledged]);

  const onHoverContent = (msg, e) => {
    if (!msg) { setHover(null); return; }
    setHover({ msg, x: e.clientX, y: e.clientY });
  };

  const possibleEntries = Object.entries(possible)
    .sort((a, b) => a[0].localeCompare(b[0]));

  return (
    <div style={{ padding: "16px 20px" }} onMouseMove={e => hover && setHover(h => h ? { ...h, x: e.clientX, y: e.clientY } : h)}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Action Centre</h2>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
            {isAdmin ? "Across the estate" : viewerOrg}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <MaskToggleButton />
          <button onClick={() => setComposeAll(true)} style={btnStyle()}>Add message to all pharmacies</button>
          <button onClick={requestSend} disabled={sending} style={btnStyle(true)}>
            {sending ? "Sending…" : "Send message to all pharmacies"}
          </button>
        </div>
      </div>

      {error && <div style={{ color: "#dc2626", padding: 12, background: "#fef2f2", borderRadius: 4, marginBottom: 12 }}>Error: {error}</div>}
      {loading && <div style={{ color: "#6b7280", padding: 24 }}>Loading…</div>}

      {!loading && (
        <>
          {/* GRID 1: awaiting acknowledgement */}
          <h3 style={{ fontSize: 15, margin: "18px 0 8px" }}>
            Messages not yet opened or acknowledged <span style={{ fontWeight: 400, color: "#6b7280" }}>({awaiting.length})</span>
          </h3>
          <MessageGrid
            rows={awaiting}
            oldestFirst
            masked={masked}
            statusLabel={(m) => (
              <span onClick={() => progressRow(m)} style={{ cursor: "pointer" }}
                    title={!m.opened ? "Not yet opened — click to mark opened (demo)" : "Opened, awaiting acknowledgement — click to acknowledge (demo)"}>
                {!m.opened ? "Not opened" : "Awaiting acknowledgement"}
              </span>
            )}
            statusColor={(m) => !m.opened
              ? { text: "#92400e", bg: "#fef3c7" }   // amber — not opened
              : { text: "#7f1d1d", bg: "#fee2e2" }}   // red — opened, awaiting ack
            onHoverContent={onHoverContent}
          />

          {/* GRID 2: possible messages to send */}
          <h3 style={{ fontSize: 15, margin: "26px 0 8px" }}>
            Current possible messages to send <span style={{ fontWeight: 400, color: "#6b7280" }}>({possibleEntries.length} {possibleEntries.length === 1 ? "pharmacy" : "pharmacies"})</span>
          </h3>
          {possibleEntries.length === 0 ? (
            <div style={{ color: "#6b7280", padding: 16, border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" }}>
              Nothing to suggest right now.
            </div>
          ) : possibleEntries.map(([pharmacy, block]) => (
            <div key={pharmacy} style={{ marginBottom: 14, border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
              <div style={{ padding: "10px 14px", borderBottom: "1px solid #eee", background: "#f9fafb", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div>
                  <span style={{ fontWeight: 600 }}>{displayName(pharmacy, masked)}</span>
                  <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 8 }}>→ {block.recipientName}</span>
                </div>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <label style={{ fontSize: 12, color: "#374151", display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}
                         title="If ticked, this pharmacy's message must be acknowledged by the recipient.">
                    <input type="checkbox" checked={!!block.requireAck} onChange={() => toggleBlockAck(pharmacy)} />
                    Require acknowledgement
                  </label>
                  <button onClick={() => addMessageToPharmacy(pharmacy)} style={btnStyle()}>+ Add another message</button>
                  <button onClick={() => setPreviewFor(pharmacy)} style={btnStyle()}>Preview</button>
                  <button onClick={() => requestSend(pharmacy)} disabled={sending} style={btnStyle(true)}>Send message to this pharmacy only</button>
                </div>
              </div>

              {/* Column headers (only when there are messages) */}
              {block.messages.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 14px", borderBottom: "1px solid #f3f4f6", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "#9ca3af" }}>
                  <div style={{ width: 60, textAlign: "center" }}>Include</div>
                  <div style={{ flex: 1 }}>Message</div>
                  <div style={{ width: 80, textAlign: "center" }}>Dismiss</div>
                </div>
              )}

              {block.messages.length === 0 && (
                <div style={{ padding: "14px", fontSize: 13, color: "#9ca3af", fontStyle: "italic" }}>
                  No current recommendations. You can still add a message below and send it.
                </div>
              )}

              {block.messages.map(m => (
                <div key={m.id} style={{
                  display: "flex", alignItems: "flex-start", gap: 12, padding: "9px 14px",
                  borderBottom: "1px solid #f3f4f6",
                  borderLeft: `3px solid ${m.kind === "summary" ? "#f59e0b" : m.kind === "nearmiss" ? "#ef4444" : "#9ca3af"}`,
                  opacity: m.dismissed ? 0.5 : 1
                }}>
                  <div style={{ width: 60, textAlign: "center", paddingTop: 6 }}>
                    <input type="checkbox" checked={m.include} disabled={m.dismissed} onChange={() => toggleMsg(pharmacy, m.id, "include")} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, textDecoration: m.dismissed ? "line-through" : "none" }}>
                    {(m.kind === "nearmiss" || m.kind === "summary") && (
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.4, color: "#9ca3af", textTransform: "uppercase", display: "block", marginBottom: 2 }}>
                        {m.kind === "nearmiss" ? "Near miss" : "Summary"}
                      </span>
                    )}
                    <input
                      value={m.subject}
                      onChange={e => editMsg(pharmacy, m.id, "subject", e.target.value)}
                      placeholder="Subject"
                      disabled={m.dismissed}
                      style={{ width: "100%", fontSize: 13, fontWeight: 600, border: "1px solid #e5e7eb", borderRadius: 4, padding: "4px 6px", marginBottom: 4, textDecoration: m.dismissed ? "line-through" : "none" }}
                    />
                    <textarea
                      value={m.body}
                      onChange={e => editMsg(pharmacy, m.id, "body", e.target.value)}
                      placeholder="Message body"
                      rows={2}
                      disabled={m.dismissed}
                      style={{ width: "100%", fontSize: 12, color: "#4b5563", border: "1px solid #e5e7eb", borderRadius: 4, padding: "4px 6px", resize: "vertical", fontFamily: "inherit" }}
                    />
                  </div>
                  <div style={{ width: 80, textAlign: "center", paddingTop: 4 }}>
                    {m.dismissed ? (
                      <span style={{ fontSize: 11, color: "#9ca3af" }}>Dismissed</span>
                    ) : (
                      <button
                        onClick={() => dismissAlert(pharmacy, m.id)}
                        title="Dismiss this suggestion so it won't be raised again"
                        style={{ fontSize: 11, padding: "4px 10px", border: "1px solid #dc2626", borderRadius: 4, background: "#fff", color: "#dc2626", cursor: "pointer" }}
                      >
                        Dismiss
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* GRID 3: acknowledged / no-ack */}
          <h3 style={{ fontSize: 15, margin: "26px 0 8px" }}>
            Acknowledged / no acknowledgement required <span style={{ fontWeight: 400, color: "#6b7280" }}>({ackTotal})</span>
          </h3>
          <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search messages…"
              style={{ flex: "1 1 240px", fontSize: 13, border: "1px solid #d1d5db", borderRadius: 4, padding: "6px 10px" }}
            />
            <select
              value={pharmacyFilter}
              onChange={e => setPharmacyFilter(e.target.value)}
              style={{ fontSize: 13, border: "1px solid #d1d5db", borderRadius: 4, padding: "6px 10px", minWidth: 200 }}
            >
              <option value="">All pharmacies</option>
              {pharmacyOptions.map(p => <option key={p.id} value={p.id}>{displayName(p.name, masked)}</option>)}
            </select>
          </div>
          <MessageGrid
            rows={acknowledged}
            oldestFirst={false}
            masked={masked}
            statusLabel={(m) => m.acknowledgedAt ? "Acknowledged" : "No acknowledgement required"}
            statusColor={{ text: "#166534", bg: "#dcfce7" }}
            onHoverContent={onHoverContent}
          />
          {acknowledged.length < ackTotal && (
            <div style={{ textAlign: "center", marginTop: 10 }}>
              <button onClick={() => loadAcknowledged(false)} style={btnStyle()}>
                Load more ({ackTotal - acknowledged.length} remaining)
              </button>
            </div>
          )}
        </>
      )}

      <ContentPopup hover={hover} />
      {previewFor && possible[previewFor] && (
        <PreviewDialog
          pharmacy={displayName(previewFor, masked)}
          recipientName={possible[previewFor].recipientName}
          messages={possible[previewFor].messages.filter(m => m.include && !m.dismissed)}
          requireAck={!!possible[previewFor].requireAck}
          onClose={() => setPreviewFor(null)}
        />
      )}
      {confirmSend && (
        <ConfirmSendDialog
          summary={confirmSend}
          masked={masked}
          sending={sending}
          onProceed={sendAll}
          onCancel={() => setConfirmSend(null)}
        />
      )}
      {composeAll && (
        <AddToAllDialog
          pharmacyCount={Object.keys(possible).length}
          onCancel={() => setComposeAll(false)}
          onAdd={(msg) => { addMessageToAll(msg); setComposeAll(false); }}
        />
      )}
    </div>
  );
}

function btnStyle(primary) {
  return {
    fontSize: 12, padding: "6px 12px", borderRadius: 4, cursor: "pointer",
    border: `1px solid ${primary ? "#1f2937" : "#9ca3af"}`,
    background: primary ? "#1f2937" : "#fff",
    color: primary ? "#fff" : "#374151",
    fontWeight: primary ? 600 : 400, whiteSpace: "nowrap"
  };
}

export default ActionCentre;
export { ActionCentre };